/**
 * Natural Language Search Service
 * Converts natural language queries into structured search results
 * Handles edge cases like "no results found" with helpful suggestions
 * Learns from user corrections to improve matching
 */

import type { Address, Vehicle, EmergencyContact, ServiceProvider, UtilityAccount } from '../types/profile';
import type { InsurancePolicy } from '../types/insurance';
import type { Reminder } from '../types/reminder';
import type { HealthProvider } from '../types/health';
import type { FinancialInstitution } from '../types/financial';

// Storage key for search corrections/learning
const NLS_CORRECTIONS_KEY = 'lucidDock_nlsCorrections';
const NLS_SYNONYMS_KEY = 'lucidDock_nlsSynonyms';

// Entity types that can be searched
export type SearchableEntityType =
  | 'address'
  | 'vehicle'
  | 'insurance'
  | 'reminder'
  | 'emergency-contact'
  | 'healthcare'
  | 'finance'
  | 'service-provider'
  | 'utility';

// Intent detected from natural language query
export interface SearchIntent {
  action: 'find' | 'show' | 'get' | 'list' | 'when' | 'who' | 'what' | 'where';
  entityType?: SearchableEntityType;
  entitySubtype?: string; // e.g., 'plumber', 'dentist', 'home', 'car'
  attributes?: string[]; // Additional search attributes
  rawQuery: string;
  normalizedQuery: string;
  confidence: number;
}

// Search result with edge case handling
export interface NLSSearchResult {
  found: boolean;
  results: SearchResultItem[];
  intent: SearchIntent;
  suggestions?: string[]; // Suggestions when no results found
  alternativeQueries?: string[]; // Related queries user might want to try
  emptyStateMessage?: string; // Helpful message for empty results
  addPrompt?: {
    entityType: SearchableEntityType;
    entitySubtype?: string;
    message: string;
  };
}

export interface SearchResultItem {
  id: string;
  type: SearchableEntityType;
  title: string;
  subtitle?: string;
  description?: string;
  relevanceScore: number;
  matchedOn: string[]; // What fields matched
  data: unknown;
}

// Learned corrections for improving search
interface SearchCorrection {
  query: string;
  intendedEntityType: SearchableEntityType;
  intendedSubtype?: string;
  timestamp: string;
  count: number;
}

// Custom synonyms learned from user behavior
interface LearnedSynonym {
  term: string;
  mappedTo: string;
  entityType: SearchableEntityType;
  count: number;
}

// Built-in synonyms and mappings
const ENTITY_KEYWORDS: Record<SearchableEntityType, string[]> = {
  'address': ['address', 'home', 'house', 'apartment', 'place', 'location', 'residence', 'property', 'living'],
  'vehicle': ['vehicle', 'car', 'truck', 'suv', 'motorcycle', 'bike', 'auto', 'automobile', 'wheels'],
  'insurance': ['insurance', 'policy', 'coverage', 'insured', 'premium', 'deductible'],
  'reminder': ['reminder', 'remind', 'task', 'todo', 'scheduled', 'appointment', 'meeting', 'event'],
  'emergency-contact': ['emergency', 'contact', 'sos', 'urgent', 'ice', 'family'],
  'healthcare': ['doctor', 'healthcare', 'health', 'medical', 'physician', 'dentist', 'specialist', 'clinic', 'hospital', 'provider'],
  'finance': ['bank', 'finance', 'financial', 'money', 'account', 'credit', 'loan', 'investment'],
  'service-provider': ['plumber', 'electrician', 'cleaner', 'hvac', 'landscaper', 'handyman', 'pest', 'contractor', 'repair', 'service', 'provider', 'maintenance'],
  'utility': ['utility', 'electric', 'electricity', 'gas', 'water', 'internet', 'cable', 'trash', 'bill']
};

// Service provider category mappings
const SERVICE_PROVIDER_CATEGORIES: Record<string, string[]> = {
  'plumber': ['plumber', 'plumbing', 'pipe', 'drain', 'water leak', 'toilet', 'faucet'],
  'electrician': ['electrician', 'electrical', 'wiring', 'outlet', 'breaker', 'light'],
  'cleaner': ['cleaner', 'cleaning', 'maid', 'housekeeper', 'janitorial'],
  'hvac': ['hvac', 'heating', 'cooling', 'ac', 'air conditioning', 'furnace', 'ventilation'],
  'landscaper': ['landscaper', 'landscaping', 'lawn', 'garden', 'yard', 'tree', 'grass'],
  'handyman': ['handyman', 'general repair', 'fix', 'maintenance'],
  'pest_control': ['pest', 'exterminator', 'bug', 'insect', 'rodent', 'termite']
};

// Insurance type mappings
const INSURANCE_TYPES: Record<string, string[]> = {
  'auto': ['auto', 'car', 'vehicle', 'motor'],
  'home': ['home', 'house', 'property', 'homeowner', 'homeowners'],
  'health': ['health', 'medical', 'healthcare'],
  'life': ['life'],
  'renters': ['renter', 'renters', 'apartment'],
  'umbrella': ['umbrella', 'excess', 'liability']
};

// Healthcare specialty mappings
const HEALTHCARE_SPECIALTIES: Record<string, string[]> = {
  'dentist': ['dentist', 'dental', 'teeth', 'orthodontist'],
  'doctor': ['doctor', 'physician', 'pcp', 'primary care', 'general'],
  'specialist': ['specialist', 'dermatologist', 'cardiologist', 'neurologist', 'orthopedic'],
  'therapist': ['therapist', 'counselor', 'psychologist', 'psychiatrist', 'mental health'],
  'optometrist': ['optometrist', 'eye doctor', 'ophthalmologist', 'vision', 'glasses']
};

// Action keywords
const ACTION_KEYWORDS: Record<string, SearchIntent['action']> = {
  'find': 'find',
  'search': 'find',
  'look': 'find',
  'show': 'show',
  'display': 'show',
  'get': 'get',
  'give': 'get',
  'list': 'list',
  'all': 'list',
  'when': 'when',
  'who': 'who',
  'what': 'what',
  'where': 'where'
};

class NaturalLanguageSearchService {
  private corrections: SearchCorrection[] = [];
  private synonyms: LearnedSynonym[] = [];

  constructor() {
    this.loadLearnings();
  }

  /**
   * Load learned corrections and synonyms from storage
   */
  private loadLearnings(): void {
    try {
      const corrections = localStorage.getItem(NLS_CORRECTIONS_KEY);
      if (corrections) {
        this.corrections = JSON.parse(corrections);
      }

      const synonyms = localStorage.getItem(NLS_SYNONYMS_KEY);
      if (synonyms) {
        this.synonyms = JSON.parse(synonyms);
      }
    } catch (error) {
      console.warn('Failed to load NLS learnings:', error);
    }
  }

  /**
   * Save learnings to storage
   */
  private saveLearnings(): void {
    try {
      localStorage.setItem(NLS_CORRECTIONS_KEY, JSON.stringify(this.corrections));
      localStorage.setItem(NLS_SYNONYMS_KEY, JSON.stringify(this.synonyms));
    } catch (error) {
      console.warn('Failed to save NLS learnings:', error);
    }
  }

  /**
   * Record a correction when user selects a different result than predicted
   */
  recordCorrection(
    query: string,
    intendedEntityType: SearchableEntityType,
    intendedSubtype?: string
  ): void {
    const normalizedQuery = query.toLowerCase().trim();

    const existing = this.corrections.find(
      c => c.query === normalizedQuery &&
           c.intendedEntityType === intendedEntityType &&
           c.intendedSubtype === intendedSubtype
    );

    if (existing) {
      existing.count++;
      existing.timestamp = new Date().toISOString();
    } else {
      this.corrections.push({
        query: normalizedQuery,
        intendedEntityType,
        intendedSubtype,
        timestamp: new Date().toISOString(),
        count: 1
      });
    }

    // Keep only top 100 corrections by count
    this.corrections.sort((a, b) => b.count - a.count);
    this.corrections = this.corrections.slice(0, 100);

    this.saveLearnings();
  }

  /**
   * Add a custom synonym mapping
   */
  addSynonym(term: string, mappedTo: string, entityType: SearchableEntityType): void {
    const normalizedTerm = term.toLowerCase().trim();

    const existing = this.synonyms.find(
      s => s.term === normalizedTerm && s.entityType === entityType
    );

    if (existing) {
      existing.mappedTo = mappedTo;
      existing.count++;
    } else {
      this.synonyms.push({
        term: normalizedTerm,
        mappedTo,
        entityType,
        count: 1
      });
    }

    this.saveLearnings();
  }

  /**
   * Parse natural language query into structured intent
   */
  parseIntent(query: string): SearchIntent {
    const normalizedQuery = query.toLowerCase().trim();
    const words = normalizedQuery.split(/\s+/);

    // Check learned corrections first
    const correction = this.corrections.find(c => c.query === normalizedQuery);
    if (correction && correction.count >= 2) {
      return {
        action: 'find',
        entityType: correction.intendedEntityType,
        entitySubtype: correction.intendedSubtype,
        attributes: [],
        rawQuery: query,
        normalizedQuery,
        confidence: 0.95 // High confidence for learned corrections
      };
    }

    // Detect action
    let action: SearchIntent['action'] = 'find';
    for (const word of words) {
      if (ACTION_KEYWORDS[word]) {
        action = ACTION_KEYWORDS[word];
        break;
      }
    }

    // Detect entity type and subtype
    let entityType: SearchableEntityType | undefined;
    let entitySubtype: string | undefined;
    let confidence = 0.5;

    // Check learned synonyms
    for (const synonym of this.synonyms) {
      if (normalizedQuery.includes(synonym.term)) {
        entityType = synonym.entityType;
        entitySubtype = synonym.mappedTo;
        confidence = 0.85;
        break;
      }
    }

    // If not found in synonyms, check built-in mappings
    if (!entityType) {
      // Check service provider categories first (most specific)
      for (const [category, keywords] of Object.entries(SERVICE_PROVIDER_CATEGORIES)) {
        if (keywords.some(kw => normalizedQuery.includes(kw))) {
          entityType = 'service-provider';
          entitySubtype = category;
          confidence = 0.9;
          break;
        }
      }

      // Check healthcare specialties
      if (!entityType) {
        for (const [specialty, keywords] of Object.entries(HEALTHCARE_SPECIALTIES)) {
          if (keywords.some(kw => normalizedQuery.includes(kw))) {
            entityType = 'healthcare';
            entitySubtype = specialty;
            confidence = 0.9;
            break;
          }
        }
      }

      // Check insurance types
      if (!entityType) {
        for (const [insType, keywords] of Object.entries(INSURANCE_TYPES)) {
          if (keywords.some(kw => normalizedQuery.includes(kw))) {
            if (normalizedQuery.includes('insurance') || normalizedQuery.includes('policy')) {
              entityType = 'insurance';
              entitySubtype = insType;
              confidence = 0.85;
              break;
            }
          }
        }
      }

      // Check general entity keywords
      if (!entityType) {
        for (const [type, keywords] of Object.entries(ENTITY_KEYWORDS)) {
          if (keywords.some(kw => normalizedQuery.includes(kw))) {
            entityType = type as SearchableEntityType;
            confidence = 0.7;
            break;
          }
        }
      }
    }

    // Extract additional attributes (numbers, names, etc.)
    const attributes: string[] = [];
    const yearMatch = normalizedQuery.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) attributes.push(`year:${yearMatch[0]}`);

    return {
      action,
      entityType,
      entitySubtype,
      attributes,
      rawQuery: query,
      normalizedQuery,
      confidence
    };
  }

  /**
   * Search for results based on parsed intent
   */
  search(
    intent: SearchIntent,
    data: {
      addresses?: Address[];
      vehicles?: Vehicle[];
      insurancePolicies?: InsurancePolicy[];
      reminders?: Reminder[];
      emergencyContacts?: EmergencyContact[];
      healthProviders?: HealthProvider[];
      financialInstitutions?: FinancialInstitution[];
      serviceProviders?: ServiceProvider[];
      utilityAccounts?: UtilityAccount[];
    }
  ): NLSSearchResult {
    const results: SearchResultItem[] = [];
    const { normalizedQuery, entityType, entitySubtype } = intent;

    // Helper function to calculate relevance score
    const calcRelevance = (text: string, matchedFields: string[]): number => {
      const words = normalizedQuery.split(/\s+/);
      let score = 0;
      for (const word of words) {
        if (text.toLowerCase().includes(word)) {
          score += 1 / words.length;
        }
      }
      // Boost score based on number of matched fields
      score += matchedFields.length * 0.1;
      return Math.min(score, 1);
    };

    // Search service providers
    if (!entityType || entityType === 'service-provider') {
      const serviceProviders = this.collectServiceProviders(data.addresses || []);

      serviceProviders.forEach(sp => {
        const matchedOn: string[] = [];
        const searchText = `${sp.name} ${sp.category} ${sp.customCategory || ''} ${sp.notes || ''}`.toLowerCase();

        // Check category match
        if (entitySubtype) {
          const categoryMatch = sp.category === entitySubtype ||
            sp.customCategory?.toLowerCase().includes(entitySubtype);
          if (categoryMatch) matchedOn.push('category');
        }

        // Check text match
        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText)) {
          if (!matchedOn.includes('category')) matchedOn.push('name');
        }

        if (matchedOn.length > 0 || (entityType === 'service-provider' && !entitySubtype)) {
          results.push({
            id: sp.id,
            type: 'service-provider',
            title: sp.name,
            subtitle: sp.customCategory || sp.category,
            description: sp.phoneNumber,
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: sp
          });
        }
      });
    }

    // Search addresses
    if (!entityType || entityType === 'address') {
      data.addresses?.forEach(addr => {
        const matchedOn: string[] = [];
        const searchText = `${addr.label || ''} ${addr.street} ${addr.city} ${addr.state} ${addr.type}`.toLowerCase();

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText)) {
          matchedOn.push('address');
          results.push({
            id: addr.id,
            type: 'address',
            title: addr.label || `${addr.type} Address`,
            subtitle: addr.street,
            description: `${addr.city}, ${addr.state} ${addr.zipCode}`,
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: addr
          });
        }
      });
    }

    // Search vehicles
    if (!entityType || entityType === 'vehicle') {
      data.vehicles?.forEach(vehicle => {
        const matchedOn: string[] = [];
        const searchText = `${vehicle.make} ${vehicle.model} ${vehicle.year} ${vehicle.color || ''} ${vehicle.type || ''}`.toLowerCase();

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText)) {
          matchedOn.push('vehicle');
          results.push({
            id: vehicle.id,
            type: 'vehicle',
            title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
            subtitle: vehicle.licensePlate || vehicle.vin || '',
            description: vehicle.color || vehicle.type || '',
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: vehicle
          });
        }
      });
    }

    // Search insurance
    if (!entityType || entityType === 'insurance') {
      data.insurancePolicies?.forEach(policy => {
        const matchedOn: string[] = [];
        const insType = policy.insuranceType || 'other';
        const searchText = `${insType} ${policy.company} insurance policy`.toLowerCase();

        // Check subtype match
        if (entitySubtype && insType === entitySubtype) {
          matchedOn.push('type');
        }

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText) || matchedOn.length > 0) {
          results.push({
            id: policy.id,
            type: 'insurance',
            title: `${insType.charAt(0).toUpperCase() + insType.slice(1)} Insurance`,
            subtitle: policy.company,
            description: policy.policyNumber ? `Policy: ****${policy.policyNumber.slice(-4)}` : '',
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: policy
          });
        }
      });
    }

    // Search healthcare providers
    if (!entityType || entityType === 'healthcare') {
      data.healthProviders?.forEach(provider => {
        const matchedOn: string[] = [];
        const searchText = `${provider.name} ${provider.specialty || ''} doctor healthcare`.toLowerCase();

        // Check specialty match
        if (entitySubtype) {
          const specialtyMatch = provider.specialty?.toLowerCase().includes(entitySubtype);
          if (specialtyMatch) matchedOn.push('specialty');
        }

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText) || matchedOn.length > 0) {
          results.push({
            id: provider.id,
            type: 'healthcare',
            title: provider.name,
            subtitle: provider.specialty || 'Healthcare Provider',
            description: provider.phone || '',
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: provider
          });
        }
      });
    }

    // Search financial institutions
    if (!entityType || entityType === 'finance') {
      data.financialInstitutions?.forEach(inst => {
        const matchedOn: string[] = [];
        const searchText = `${inst.name} ${inst.type || ''} bank finance`.toLowerCase();

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText)) {
          matchedOn.push('name');
          results.push({
            id: inst.id,
            type: 'finance',
            title: inst.name,
            subtitle: inst.type || 'Financial Institution',
            description: '',
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: inst
          });
        }
      });
    }

    // Search reminders
    if (!entityType || entityType === 'reminder') {
      data.reminders?.forEach(reminder => {
        const matchedOn: string[] = [];
        const searchText = `${reminder.title} ${reminder.description || ''}`.toLowerCase();

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText)) {
          matchedOn.push('title');
          results.push({
            id: reminder.id,
            type: 'reminder',
            title: reminder.title,
            subtitle: reminder.description || '',
            description: `${reminder.date} at ${reminder.time}`,
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: reminder
          });
        }
      });
    }

    // Search emergency contacts
    if (!entityType || entityType === 'emergency-contact') {
      data.emergencyContacts?.forEach(contact => {
        const matchedOn: string[] = [];
        const searchText = `${contact.name} ${contact.relationship} emergency contact`.toLowerCase();

        if (searchText.includes(normalizedQuery) || this.fuzzyMatch(normalizedQuery, searchText)) {
          matchedOn.push('name');
          results.push({
            id: contact.id,
            type: 'emergency-contact',
            title: contact.name,
            subtitle: contact.relationship,
            description: contact.phoneNumber,
            relevanceScore: calcRelevance(searchText, matchedOn),
            matchedOn,
            data: contact
          });
        }
      });
    }

    // Sort by relevance
    results.sort((a, b) => b.relevanceScore - a.relevanceScore);

    // Handle no results - provide helpful suggestions
    if (results.length === 0) {
      return this.buildEmptyStateResponse(intent, data);
    }

    return {
      found: true,
      results,
      intent
    };
  }

  /**
   * Collect all service providers from addresses
   */
  private collectServiceProviders(addresses: Address[]): ServiceProvider[] {
    const providers: ServiceProvider[] = [];
    addresses.forEach(addr => {
      if (addr.serviceProviders) {
        providers.push(...addr.serviceProviders);
      }
    });
    return providers;
  }

  /**
   * Simple fuzzy matching for typo tolerance
   */
  private fuzzyMatch(query: string, text: string): boolean {
    // Simple character overlap check
    const queryChars = new Set(query.toLowerCase());
    const textChars = new Set(text.toLowerCase());
    let overlap = 0;
    queryChars.forEach(c => {
      if (textChars.has(c)) overlap++;
    });
    return overlap / queryChars.size > 0.7;
  }

  /**
   * Build helpful empty state response with suggestions
   */
  private buildEmptyStateResponse(
    intent: SearchIntent,
    data: {
      addresses?: Address[];
      vehicles?: Vehicle[];
      insurancePolicies?: InsurancePolicy[];
      healthProviders?: HealthProvider[];
      financialInstitutions?: FinancialInstitution[];
    }
  ): NLSSearchResult {
    const { entityType, entitySubtype, normalizedQuery } = intent;
    const suggestions: string[] = [];
    const alternativeQueries: string[] = [];
    let emptyStateMessage = `No results found for "${normalizedQuery}"`;
    let addPrompt: NLSSearchResult['addPrompt'] | undefined;

    // Service provider not found
    if (entityType === 'service-provider' && entitySubtype) {
      const categoryLabel = entitySubtype.replace('_', ' ');
      emptyStateMessage = `You haven't added any ${categoryLabel}s yet`;
      addPrompt = {
        entityType: 'service-provider',
        entitySubtype,
        message: `Would you like to add a ${categoryLabel}?`
      };

      // Suggest related service providers
      const relatedCategories = Object.keys(SERVICE_PROVIDER_CATEGORIES)
        .filter(c => c !== entitySubtype)
        .slice(0, 3);
      suggestions.push(`Try searching for: ${relatedCategories.join(', ')}`);
    }

    // Healthcare provider not found
    else if (entityType === 'healthcare' && entitySubtype) {
      emptyStateMessage = `You haven't added any ${entitySubtype}s yet`;
      addPrompt = {
        entityType: 'healthcare',
        entitySubtype,
        message: `Would you like to add a ${entitySubtype}?`
      };

      // Check if any healthcare providers exist
      if (data.healthProviders && data.healthProviders.length > 0) {
        const specialties = [...new Set(data.healthProviders.map(p => p.specialty).filter(Boolean))];
        if (specialties.length > 0) {
          suggestions.push(`You have providers in: ${specialties.slice(0, 3).join(', ')}`);
        }
      }
    }

    // Insurance not found
    else if (entityType === 'insurance' && entitySubtype) {
      emptyStateMessage = `You haven't added ${entitySubtype} insurance yet`;
      addPrompt = {
        entityType: 'insurance',
        entitySubtype,
        message: `Would you like to add ${entitySubtype} insurance?`
      };

      // Show available insurance types
      if (data.insurancePolicies && data.insurancePolicies.length > 0) {
        const types = [...new Set(data.insurancePolicies.map(p => p.insuranceType).filter(Boolean))];
        if (types.length > 0) {
          suggestions.push(`You have: ${types.join(', ')} insurance`);
        }
      }
    }

    // Address not found
    else if (entityType === 'address') {
      if (data.addresses && data.addresses.length === 0) {
        emptyStateMessage = "You haven't added any addresses yet";
        addPrompt = {
          entityType: 'address',
          message: 'Would you like to add an address?'
        };
      }
    }

    // Vehicle not found
    else if (entityType === 'vehicle') {
      if (data.vehicles && data.vehicles.length === 0) {
        emptyStateMessage = "You haven't added any vehicles yet";
        addPrompt = {
          entityType: 'vehicle',
          message: 'Would you like to add a vehicle?'
        };
      }
    }

    // Generic empty state
    else {
      suggestions.push('Try using different keywords');
      alternativeQueries.push('show all addresses', 'list my vehicles', 'find my insurance');
    }

    return {
      found: false,
      results: [],
      intent,
      suggestions,
      alternativeQueries,
      emptyStateMessage,
      addPrompt
    };
  }

  /**
   * Get search suggestions based on partial query
   */
  getSuggestions(partialQuery: string): string[] {
    const suggestions: string[] = [];
    const query = partialQuery.toLowerCase().trim();

    if (query.length < 2) return suggestions;

    // Suggest entity types
    for (const [, keywords] of Object.entries(ENTITY_KEYWORDS)) {
      for (const kw of keywords) {
        if (kw.startsWith(query)) {
          suggestions.push(`Show my ${kw}`);
          break;
        }
      }
    }

    // Suggest service provider categories
    for (const [category, keywords] of Object.entries(SERVICE_PROVIDER_CATEGORIES)) {
      for (const kw of keywords) {
        if (kw.startsWith(query)) {
          suggestions.push(`Find ${category}`);
          break;
        }
      }
    }

    // Add learned queries
    this.corrections
      .filter(c => c.query.startsWith(query))
      .slice(0, 3)
      .forEach(c => suggestions.push(c.query));

    return [...new Set(suggestions)].slice(0, 5);
  }

  /**
   * Clear all learned data
   */
  clearLearnings(): void {
    this.corrections = [];
    this.synonyms = [];
    localStorage.removeItem(NLS_CORRECTIONS_KEY);
    localStorage.removeItem(NLS_SYNONYMS_KEY);
  }

  /**
   * Get learning statistics
   */
  getStats(): { corrections: number; synonyms: number; topQueries: string[] } {
    return {
      corrections: this.corrections.length,
      synonyms: this.synonyms.length,
      topQueries: this.corrections
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
        .map(c => c.query)
    };
  }
}

export const naturalLanguageSearchService = new NaturalLanguageSearchService();
